import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { Calendar } from './ui/calendar';
import { useToast } from './ui/use-toast';
import { getRaids, createSchedule, Raid } from '@/api/scheduleApi';
import { Check } from 'lucide-react';
import { ko } from 'date-fns/locale';
import { RAID_CATEGORIES } from '@/constants/raids';

interface ScheduleCreateModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

// 시간 유효성 검사
const isValidTime = (time: string): boolean => {
  const timeRegex = /^([01]\d|2[0-3]):([0-5]\d)$/;
  return timeRegex.test(time);
};

export default function ScheduleCreateModal({ open, onOpenChange, onSuccess }: ScheduleCreateModalProps) {
  const { toast } = useToast();

  // 레이드 목록
  const [raids, setRaids] = useState<Raid[]>([]);
  const [loading, setLoading] = useState(false);

  // 폼 데이터
  const [selectedRaidTitle, setSelectedRaidTitle] = useState('');
  const [freeRaidInput, setFreeRaidInput] = useState('');
  const [content, setContent] = useState('');
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState('21:00');
  const [isDouble, setIsDouble] = useState(false);

  /**
   * 다음 화요일 계산 (오늘부터 다음 주 화요일까지)
   */
  const getNextTuesday = (): Date => {
    const now = new Date();
    const currentDay = now.getDay(); // 0=일, 1=월, 2=화, 3=수, 4=목, 5=금, 6=토

    let daysToAdd = 0;
    if (currentDay === 0) { // 일요일
      daysToAdd = 9; // 다음 주 화요일 (9일 후)
    } else if (currentDay === 1) { // 월요일
      daysToAdd = 8; // 다음 주 화요일 (8일 후)
    } else if (currentDay === 2) { // 화요일
      daysToAdd = 7; // 다음 주 화요일 (7일 후)
    } else { // 수, 목, 금, 토 (3, 4, 5, 6)
      daysToAdd = 9 - currentDay; // 다음 주 화요일
    }

    const nextTuesday = new Date(now);
    nextTuesday.setDate(now.getDate() + daysToAdd);
    return nextTuesday;
  };

  /**
   * 레이드 목록 로드
   */
  useEffect(() => {
    if (open) {
      loadRaids();
      // 기본값 설정: 오늘을 기본값으로
      const today = new Date();
      setSelectedDate(today);
      setSelectedTime('21:00');
    } else {
      // 모달 닫힐 때 초기화
      setSelectedRaidTitle('');
      setFreeRaidInput('');
      setContent('');
      setIsDouble(false);
    }
  }, [open]);

  const loadRaids = async () => {
    try {
      const data = await getRaids();
      setRaids(data);
    } catch (error) {
      console.error('레이드 목록 로드 실패:', error);
      toast({
        title: '오류',
        description: '레이드 목록을 불러오는데 실패했습니다.',
        variant: 'destructive',
      });
    }
  };

  /**
   * 레이드 선택
   */
  const handleRaidSelect = (raidTitle: string) => {
    setSelectedRaidTitle(raidTitle);
    setFreeRaidInput('');
  };

  /**
   * 자유모집 확인
   */
  const handleFreeRaidConfirm = () => {
    if (freeRaidInput.trim() === '') {
      toast({
        title: '오류',
        description: '내용을 입력해주세요.',
        variant: 'destructive',
      });
      return;
    }
    setSelectedRaidTitle(freeRaidInput);
  };

  /**
   * 카테고리별 레이드 가져오기
   */
  const getRaidsForCategory = (raidSeqs: number[] | 'others'): Raid[] => {
    if (raidSeqs === 'others') {
      const usedSeqs = RAID_CATEGORIES
        .filter((cat) => Array.isArray(cat.raidSeqs))
        .flatMap((cat) => cat.raidSeqs as number[]);
      return raids.filter((raid) => !usedSeqs.includes(raid.seq));
    }
    return raids.filter((raid) => raidSeqs.includes(raid.seq));
  };

  /**
   * 폼 제출
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 유효성 검사
    if (!selectedRaidTitle) {
      toast({
        title: '오류',
        description: '레이드를 선택해주세요.',
        variant: 'destructive',
      });
      return;
    }

    if (!selectedDate) {
      toast({
        title: '오류',
        description: '날짜를 선택해주세요.',
        variant: 'destructive',
      });
      return;
    }

    if (!isValidTime(selectedTime)) {
      toast({
        title: '오류',
        description: '올바른 시간 형식을 입력해주세요 (HH:mm).',
        variant: 'destructive',
      });
      return;
    }

    if (content.length > 40) {
      toast({
        title: '오류',
        description: '메모는 40자 이내로 입력해주세요.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      // 날짜 포맷팅
      const year = selectedDate.getFullYear();
      const month = String(selectedDate.getMonth() + 1).padStart(2, '0');
      const day = String(selectedDate.getDate()).padStart(2, '0');
      const startTimeStr = `${year}-${month}-${day} ${selectedTime}`;

      await createSchedule({
        raidTitle: selectedRaidTitle,
        content,
        startTime: startTimeStr,
        isDouble: isDouble ? 1 : 0,
      });

      toast({
        title: '성공',
        description: '스케줄이 생성되었습니다.',
      });

      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error('스케줄 생성 실패:', error);
      toast({
        title: '오류',
        description: '스케줄 생성에 실패했습니다.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col p-0">
        <DialogHeader className="px-4 pt-4 pb-3">
          <DialogTitle className="text-lg">레이드 스케줄 생성</DialogTitle>
          <DialogDescription className="text-sm">
            레이드 일정을 생성합니다
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          {/* Desktop: 2단 레이아웃 / Mobile: 1단 */}
          <div className="grid md:grid-cols-2 gap-4 px-4 pb-4">
            {/* 왼쪽: 레이드 선택 */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-semibold">레이드 선택 *</Label>
                {selectedRaidTitle && (
                  <div className="bg-accent/20 border border-accent rounded-md p-2 text-xs">
                    <span className="text-muted-foreground">선택됨:</span>{' '}
                    <span className="font-semibold text-accent-foreground">{selectedRaidTitle}</span>
                  </div>
                )}
              </div>

              <Accordion type="single" collapsible className="border rounded-md">
                {RAID_CATEGORIES.map((category) => (
                  <AccordionItem key={category.id} value={category.id}>
                    <AccordionTrigger className="px-3 py-3.5 text-sm hover:no-underline">
                      {category.title}
                    </AccordionTrigger>
                    <AccordionContent className="px-3 pb-3">
                      {category.id === 'free' ? (
                        <div className="flex gap-2">
                          <Input
                            placeholder="자유모집 제목 입력"
                            value={freeRaidInput}
                            onChange={(e) => setFreeRaidInput(e.target.value)}
                            className="flex-1 text-sm h-9"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleFreeRaidConfirm}
                            size="icon"
                            className="h-9 w-9"
                          >
                            <Check className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ) : (
                        <div className="grid gap-2 max-h-80 overflow-y-auto">
                          {getRaidsForCategory(category.raidSeqs).map((raid) => (
                            <div
                              key={raid.seq}
                              onClick={() => handleRaidSelect(raid.raidTitle)}
                              className={`
                                flex items-center justify-between p-2.5 rounded-md border cursor-pointer
                                transition-colors hover:bg-accent/10
                                ${selectedRaidTitle === raid.raidTitle ? 'bg-accent/20 border-accent' : 'border-border'}
                              `}
                            >
                              <div className="space-y-0.5">
                                <div className="font-medium text-sm leading-tight">{raid.raidTitle}</div>
                                <div className="text-xs text-muted-foreground">
                                  Lv.{raid.levelCut}
                                </div>
                              </div>
                              {selectedRaidTitle === raid.raidTitle && (
                                <Check className="w-4 h-4 text-accent-foreground" />
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>

            {/* 오른쪽: 날짜/시간 및 옵션 */}
            <div className="space-y-4">
              {/* 날짜 선택 */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold">날짜 선택 *</Label>
                <div className="border rounded-md p-3 flex justify-center bg-muted/30">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    locale={ko}
                    disabled={(date) => {
                      const today = new Date();
                      const nextTue = getNextTuesday();

                      // 타임존 이슈 방지: 연/월/일만 비교
                      const dateYMD = date.getFullYear() * 10000 + date.getMonth() * 100 + date.getDate();
                      const todayYMD = today.getFullYear() * 10000 + today.getMonth() * 100 + today.getDate();
                      const nextTueYMD = nextTue.getFullYear() * 10000 + nextTue.getMonth() * 100 + nextTue.getDate();

                      // 오늘 이전이거나 다음 화요일 이후면 비활성화
                      return dateYMD < todayYMD || dateYMD > nextTueYMD;
                    }}
                    className="rounded-md w-full text-sm"
                  />
                </div>
                <p className="text-[11px] text-muted-foreground/80">
                  * 오늘부터 다음 주 화요일까지 생성 가능
                </p>
              </div>

              {/* 시간 선택 */}
              <div className="space-y-2">
                <Label htmlFor="time" className="text-sm font-semibold">시간 선택 *</Label>
                <Input
                  id="time"
                  type="time"
                  value={selectedTime}
                  onChange={(e) => setSelectedTime(e.target.value)}
                  className="text-sm h-9"
                />
              </div>

              {/* 메모 */}
              <div className="space-y-2">
                <Label htmlFor="content" className="text-sm font-semibold">메모 (선택, 최대 40자)</Label>
                <Textarea
                  id="content"
                  placeholder="ex) 숙제팟"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  maxLength={40}
                  rows={3}
                  className="resize-none text-sm"
                />
                <p className="text-[11px] text-muted-foreground/80 text-right">{content.length}/40</p>
              </div>

              {/* 이중 레이드 - 임시 비활성화 */}
              {/* <div className="flex items-center space-x-2 bg-accent/20 border border-accent p-2.5 rounded-md">
                <Checkbox
                  id="isDouble"
                  checked={isDouble}
                  onCheckedChange={(checked) => setIsDouble(checked as boolean)}
                />
                <Label htmlFor="isDouble" className="text-xs font-medium cursor-pointer flex-1">
                  본1부1로 생성하기 (캐릭터당 2회 참여 가능)
                </Label>
              </div> */}
            </div>
          </div>

          {/* 하단 버튼 */}
          <div className="flex gap-2 px-4 py-3 border-t bg-muted/30">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="flex-1 h-9 text-sm"
            >
              취소
            </Button>
            <Button type="submit" disabled={loading} className="flex-1 h-9 text-sm">
              {loading ? '생성 중...' : '일정 생성'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
